import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../App';
import Icon from '../components/Icon';
import './Chat.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Fallback-Beispiele, falls /capabilities nicht erreichbar ist.
const FALLBACK_EXAMPLES = [
  'Was können Sie für mich tun?',
  'Hilf mir, eine Eigentümerversammlung vorzubereiten.',
  'Exportiere die Stammdaten eines Objekts.',
];

// --- Session-Persistenz (client-seitig, pro Nutzer) -------------------------
// Der Operator ist server-stateless; der Verlauf lebte bisher nur im React-State
// und ging bei Tab-/Routenwechsel verloren. Wir persistieren ihn in localStorage,
// namespaced pro user.id (Transkripte koennen Kundendaten enthalten -> nicht
// nutzeruebergreifend teilen). ponytail: kein Backend/DB noetig fuer die
// Experimentierphase; Upgrade auf Server-Sessions ist ein reiner Storage-Swap.
const IDLE_MS = 30 * 60 * 1000; // nach 30 Min Inaktivitaet -> neuer Chat, alter wandert in den Verlauf
const MAX_SESSIONS = 30;        // aeltere fallen raus (localStorage-Quota)

const lsKey = (userId) => `oi_operator_sessions_${userId}`;
const makeId = () => `${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

function loadSessions(userId) {
  try {
    const raw = JSON.parse(localStorage.getItem(lsKey(userId)) || '[]');
    if (!Array.isArray(raw)) return [];
    return raw.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  } catch {
    return [];
  }
}

function saveSessions(userId, list) {
  try {
    localStorage.setItem(lsKey(userId), JSON.stringify(list.slice(0, MAX_SESSIONS)));
  } catch {
    // Quota/Private-Mode: Verlauf ist best-effort, nie die Seite crashen lassen.
  }
}

function sessionTitle(msgs) {
  const first = (msgs || []).find((m) => m.role === 'user' && (m.content || '').trim());
  return first ? first.content.trim().slice(0, 42) : 'Neuer Chat';
}

function relTime(ts) {
  if (!ts) return '';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'gerade eben';
  const m = Math.floor(s / 60);
  if (m < 60) return `vor ${m} Min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std`;
  const d = Math.floor(h / 24);
  return `vor ${d} T`;
}

// Versucht, den Tool-Inhalt als JSON zu lesen und eine files-Liste herauszuziehen.
function extractFiles(content) {
  if (typeof content !== 'string') return [];
  try {
    const parsed = JSON.parse(content);
    const files = parsed && parsed.files;
    return Array.isArray(files) ? files.filter((f) => typeof f === 'string') : [];
  } catch {
    return [];
  }
}

// /outputs ist evtl. schon im Pfad enthalten -> nicht doppelt voranstellen.
function fileHref(path, token) {
  const base = path.startsWith('/outputs') ? path : `/outputs/${path}`;
  return `${base}?token=${token}`;
}

function FileLinks({ files, token }) {
  if (!files.length) return null;
  return (
    <div className="operator__files">
      {files.map((f, i) => (
        <a key={i} className="operator__file" href={fileHref(f, token)} download>
          <Icon name="document" size={14} />
          <span>{f.split('/').pop()}</span>
        </a>
      ))}
    </div>
  );
}

export default function Chat() {
  const { token, user } = useAuth();
  const userId = user?.id;
  const [status, setStatus] = useState(null); // null=lädt, {enabled, model}
  const [examples, setExamples] = useState(FALLBACK_EXAMPLES);
  const [sessions, setSessions] = useState([]); // Verlauf (persistiert)
  const [currentId, setCurrentId] = useState(null);
  const [ready, setReady] = useState(false); // erst nach Hydration persistieren
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState([]); // File[]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // nur mobil relevant
  const threadRef = useRef(null);
  const fileRef = useRef(null);
  const dragDepth = useRef(0); // zaehlt enter/leave, damit Kind-Elemente das Overlay nicht flackern lassen
  const lastActivity = useRef(0); // Zeitstempel der letzten Nachricht (fuer 30-Min-Regel)
  // Kontext-Grenze: ab diesem Index wird die Historie ans LLM geschickt. Alles davor bleibt
  // im sichtbaren Verlauf, geht aber NICHT mehr an das Modell (Token-Kontrolle + frischer
  // Vorgang). Pro Session persistiert.
  const contextStart = useRef(0);

  // Wenn nur Dokumente (kein Text) gesendet werden: den Operator zur Erkennung anstossen.
  const DEFAULT_DOC_PROMPT =
    'Bitte erkennen Sie die angehängten Dokumente und schlagen Sie passende Funktionen zur Weiterverarbeitung vor.';

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  // Status + Beispiel-Prompts beim Mount laden.
  useEffect(() => {
    if (!token) return;
    let active = true;
    const headers = { Authorization: `Bearer ${token}` };

    fetch(`${API_URL}/operator/status`, { headers })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((d) => { if (active) setStatus(d); })
      .catch(() => { if (active) setStatus({ enabled: false }); });

    fetch(`${API_URL}/operator/capabilities`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!active || !d || !Array.isArray(d.skills)) return;
        const picks = d.skills.slice(0, 4).map((s) => `Wie nutze ich „${s.name}“?`);
        if (picks.length) setExamples(picks);
      })
      .catch(() => {});

    return () => { active = false; };
  }, [token]);

  // Hydration: Verlauf laden. Letzte Session <30 Min -> fortsetzen, sonst neuer Chat.
  useEffect(() => {
    if (!userId) return;
    const list = loadSessions(userId);
    setSessions(list);
    const newest = list[0];
    if (newest && Date.now() - (newest.updatedAt || 0) <= IDLE_MS && (newest.messages || []).length) {
      setCurrentId(newest.id);
      setMessages(newest.messages || []);
      contextStart.current = newest.contextStart || 0;
      lastActivity.current = newest.updatedAt || 0;
    } else {
      setCurrentId(makeId());
      setMessages([]);
      contextStart.current = 0;
      lastActivity.current = 0;
    }
    setReady(true);
  }, [userId]);

  // Persistenz: bei jeder Nachrichtenaenderung die aktuelle Session upserten.
  useEffect(() => {
    if (!ready || !userId || !currentId) return;
    const now = Date.now();
    if (messages.length) lastActivity.current = now;
    setSessions((prev) => {
      let next;
      if (!messages.length) {
        next = prev.filter((s) => s.id !== currentId); // leere Session nicht persistieren
      } else {
        const existing = prev.find((s) => s.id === currentId);
        const entry = {
          id: currentId,
          title: sessionTitle(messages),
          messages,
          contextStart: contextStart.current,
          createdAt: existing?.createdAt || now,
          updatedAt: now,
        };
        next = [entry, ...prev.filter((s) => s.id !== currentId)];
      }
      next.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
      next = next.slice(0, MAX_SESSIONS);
      saveSessions(userId, next);
      return next;
    });
  }, [messages, currentId, ready, userId]);

  const newChat = useCallback(() => {
    if (loading) return;
    // Aktuelle Session ist bereits per Effect persistiert.
    setCurrentId(makeId());
    setMessages([]);
    setInput('');
    setAttachments([]);
    setError('');
    setSidebarOpen(false);
    contextStart.current = 0;
    lastActivity.current = 0;
  }, [loading]);

  // 30-Min-Regel beim Zurueckkehren zum Tab: alte Session bleibt im Verlauf, neuer Chat oeffnet.
  useEffect(() => {
    function onVis() {
      if (document.visibilityState !== 'visible' || loading) return;
      if (lastActivity.current && Date.now() - lastActivity.current > IDLE_MS && messages.length) {
        newChat();
      }
    }
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loading, messages.length, newChat]);

  function selectSession(id) {
    if (loading) return;
    const s = sessions.find((x) => x.id === id);
    if (!s) return;
    setCurrentId(id);
    setMessages(s.messages || []);
    contextStart.current = s.contextStart || 0;
    lastActivity.current = s.updatedAt || 0;
    setInput('');
    setAttachments([]);
    setError('');
    setSidebarOpen(false);
  }

  // Ein abgeschlossener Skill (Tool-Result status "done") = erledigter Vorgang.
  function isResolved(added) {
    return added.some((m) => {
      if (m.role !== 'tool' || typeof m.content !== 'string') return false;
      try { return JSON.parse(m.content).status === 'done'; } catch { return false; }
    });
  }

  async function sendTurn(nextMessages, files) {
    setError('');
    setLoading(true);
    // Nur das aktuelle Kontext-Segment ans LLM (Token-Kontrolle). Sichtbarer Verlauf bleibt voll.
    const segment = nextMessages.slice(contextStart.current);
    try {
      let res;
      if (files && files.length) {
        const form = new FormData();
        form.append('messages', JSON.stringify(segment));
        files.forEach((f) => form.append('files', f));
        res = await fetch(`${API_URL}/operator/send-with-file`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }, // KEIN Content-Type: Boundary setzt der Browser
          body: form,
        });
      } else {
        res = await fetch(`${API_URL}/operator/send`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: segment }),
        });
      }
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      const added = Array.isArray(data.messages) ? data.messages : [];
      let full = [...nextMessages, ...added];
      if (isResolved(added)) {
        // Vorgang erledigt: sichtbaren Trenner setzen + Kontext fuer den naechsten Turn zuruecksetzen.
        full = [...full, { role: 'divider', content: 'Vorgang abgeschlossen' }];
        contextStart.current = full.length;
      }
      setMessages(full);
    } catch {
      setError('Der Operator ist gerade nicht erreichbar. Bitte versuchen Sie es erneut.');
      setMessages(nextMessages); // Nutzer-Nachricht bleibt sichtbar
    } finally {
      setLoading(false);
    }
  }

  function submit(e) {
    e.preventDefault();
    if (loading) return;
    const text = input.trim();
    if (text.length < 1 && attachments.length === 0) return;
    const files = attachments;
    // Nur Datei, kein Text -> Standard-Prompt, damit der Operator das Dokument verarbeitet.
    const content = text.length ? text : DEFAULT_DOC_PROMPT;
    const userMsg = { role: 'user', content };
    if (files.length) userMsg._files = files.map((f) => f.name); // im Verlauf als kleine Anhang-Notiz
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setAttachments([]);
    sendTurn(next, files);
  }

  function seed(text) {
    if (loading) return;
    const files = attachments;
    const userMsg = { role: 'user', content: text };
    if (files.length) userMsg._files = files.map((f) => f.name);
    const next = [...messages, userMsg];
    setMessages(next);
    setAttachments([]);
    sendTurn(next, files);
  }

  // "Problem melden": schickt den VOLLEN sichtbaren Verlauf ans Ticket (ueber Kontext-
  // Grenzen hinweg). Bug-Tickets laufen die Autofixer-Pipeline. Backend fuellt Betreff/
  // Kategorie per Kurzsummary.
  async function reportIssue() {
    if (loading || !messages.length) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/operator/ticket`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setMessages((prev) => [...prev, { role: 'assistant', content: data.message || 'Ticket wurde eroeffnet.' }]);
    } catch {
      setError('Das Ticket konnte nicht eroeffnet werden. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  }

  function addFiles(picked) {
    const list = Array.from(picked || []).filter(Boolean);
    if (list.length) setAttachments((prev) => [...prev, ...list]);
  }

  function onPickFiles(e) {
    addFiles(e.target.files);
    e.target.value = ''; // erlaubt erneutes Auswaehlen derselben Datei
  }

  function removeAttachment(idx) {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }

  // --- Drag & Drop in den Chat ---
  function onDragEnter(e) {
    if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) {
      e.preventDefault();
      dragDepth.current += 1;
      setDragOver(true);
    }
  }
  function onDragOver(e) {
    if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) {
      e.preventDefault(); // noetig, damit onDrop feuert
    }
  }
  function onDragLeave() {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  }
  function onDrop(e) {
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
      addFiles(e.dataTransfer.files);
    }
  }

  // Sichtbare Nachrichten: user + assistant mit Inhalt.
  // Tool-Nachrichten + leere Assistant-Turns werden ausgeblendet,
  // ihr files[]-Inhalt wird aber als Download-Links gerendert.
  function renderMessage(m, i) {
    if (m.role === 'divider') {
      return (
        <div className="operator__divider" key={i}>
          <span>{m.content || 'Vorgang abgeschlossen'}</span>
        </div>
      );
    }

    if (m.role === 'user') {
      const upFiles = Array.isArray(m._files) ? m._files : [];
      return (
        <div className="operator__msg operator__msg--user" key={i}>
          <div className="operator__bubble">{m.content}</div>
          {upFiles.length > 0 && (
            <div className="operator__upfiles">
              {upFiles.map((name, k) => (
                <span className="operator__upfile" key={k}>
                  <Icon name="document" size={13} />
                  <span>{name}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (m.role === 'tool') {
      const files = extractFiles(m.content);
      if (!files.length) return null;
      return (
        <div className="operator__msg operator__msg--bot" key={i}>
          <FileLinks files={files} token={token} />
        </div>
      );
    }

    if (m.role === 'assistant') {
      const hasContent = typeof m.content === 'string' && m.content.trim().length > 0;
      const hasToolCalls = Array.isArray(m.tool_calls) && m.tool_calls.length > 0;
      if (!hasContent && !hasToolCalls) return null;
      return (
        <div className="operator__msg operator__msg--bot" key={i}>
          {hasContent && <div className="operator__bubble">{m.content}</div>}
          {!hasContent && hasToolCalls && (
            <span className="operator__tool-chip">
              <Icon name="settings" size={13} />
              <span>nutzt eine Funktion …</span>
            </span>
          )}
        </div>
      );
    }

    return null;
  }

  // Ladezustand
  if (status === null) {
    return (
      <div className="operator">
        <div className="operator__header">
          <div className="fleet-label">Plattform</div>
          <h1>Operator</h1>
        </div>
        <div className="operator__disabled">Wird geladen …</div>
      </div>
    );
  }

  // Nicht aktiviert
  if (!status.enabled) {
    return (
      <div className="operator">
        <div className="operator__header">
          <div className="fleet-label">Plattform</div>
          <h1>Operator</h1>
        </div>
        <div className="operator__disabled">
          Der Operator ist in Ihrem Zugang noch nicht aktiviert. Er wird in Kürze
          freigeschaltet und führt Sie dann per Dialog durch die Funktionen der Plattform.
        </div>
      </div>
    );
  }

  const empty = messages.length === 0;
  const canSend = !loading && (input.trim().length > 0 || attachments.length > 0);

  return (
    <div className="operator">
      <div className="operator__header">
        <div className="fleet-label">Plattform</div>
        <h1>Operator</h1>
        {status.model && <div className="operator__model">Modell: {status.model}</div>}
        <button
          type="button"
          className="operator__histtoggle"
          onClick={() => setSidebarOpen((v) => !v)}
          title="Verlauf ein-/ausblenden"
        >
          Verlauf
        </button>
        <button
          type="button"
          className="operator__report"
          onClick={reportIssue}
          disabled={loading || empty}
          title="Problem aus diesem Chat melden"
        >
          Problem melden
        </button>
      </div>

      <div className="operator__body">
        <aside className={`operator__sidebar${sidebarOpen ? ' operator__sidebar--open' : ''}`}>
          <button type="button" className="operator__newchat" onClick={newChat} disabled={loading}>
            + Neuer Chat
          </button>
          <div className="operator__sidebar-label">Verlauf</div>
          <div className="operator__sessions">
            {sessions.length === 0 && (
              <div className="operator__sidebar-empty">Noch keine gespeicherten Chats.</div>
            )}
            {sessions.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`operator__session${s.id === currentId ? ' operator__session--active' : ''}`}
                onClick={() => selectSession(s.id)}
                disabled={loading}
                title={s.title}
              >
                <span className="operator__session-title">{s.title}</span>
                <span className="operator__session-time">{relTime(s.updatedAt)}</span>
              </button>
            ))}
          </div>
        </aside>

        <div
          className={`operator__chat${dragOver ? ' operator__chat--dragover' : ''}`}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          {dragOver && (
            <div className="operator__dropzone">
              <Icon name="document" size={28} />
              <span>Dokumente hier ablegen</span>
            </div>
          )}
          <div className="operator__thread" ref={threadRef}>
            {empty && (
              <div className="operator__welcome">
                <p>
                  Der Operator führt Sie per Dialog durch die Funktionen der Plattform.
                  Beschreiben Sie, was Sie erledigen möchten, oder wählen Sie ein Beispiel.
                </p>
                <div className="operator__examples">
                  {examples.map((q) => (
                    <button key={q} type="button" onClick={() => seed(q)}>{q}</button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => renderMessage(m, i))}

            {loading && (
              <div className="operator__msg operator__msg--bot">
                <div className="operator__bubble operator__typing"><span /><span /><span /></div>
              </div>
            )}
          </div>

          {error && <div className="operator__notice operator__notice--error">{error}</div>}

          {attachments.length > 0 && (
            <div className="operator__attachments">
              {attachments.map((f, i) => (
                <span className="operator__attachment" key={`${f.name}-${i}`}>
                  <Icon name="document" size={13} />
                  <span>{f.name}</span>
                  <button type="button" aria-label="Anhang entfernen" onClick={() => removeAttachment(i)}>×</button>
                </span>
              ))}
            </div>
          )}

          <form className="operator__composer" onSubmit={submit}>
            <button
              type="button"
              className="operator__attach"
              aria-label="Datei anhängen"
              title="Datei anhängen"
              disabled={loading}
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              <Icon name="document" size={18} />
            </button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Was möchten Sie erledigen?"
              aria-label="Nachricht an den Operator"
            />
            <input
              type="file"
              ref={fileRef}
              multiple
              onChange={onPickFiles}
              style={{ display: 'none' }}
            />
            <button type="submit" disabled={!canSend}>
              {loading ? '…' : 'Senden'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
